import { httpClient } from '@/core/api/http-client'
import type { Category, CategoryInput } from '@/domain/entities/category.entity'
import type { Paged, PageParams } from '@/domain/entities/paging'
import type { CategoryRepository } from '@/domain/repositories/category.repository'
import { unwrap, unwrapList, unwrapPaged } from '@/infrastructure/shared/unwrap'
import type { CategoryDto } from './category.dto'
import { categoryMapper } from './category.mapper'

export class CategoryHttpRepository implements CategoryRepository {
  async list(outletId: string, params: PageParams): Promise<Paged<Category>> {
    const raw = await httpClient.get<unknown>('/categories', {
      params: {
        outlet_id: outletId,
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
      },
    })
    const paged = unwrapPaged<CategoryDto>(raw, params.page, params.limit)
    return { ...paged, items: paged.items.map(categoryMapper.toCategory) }
  }

  async listAll(outletId: string): Promise<Category[]> {
    const raw = await httpClient.get<unknown>('/categories', {
      params: { outlet_id: outletId, limit: 100 },
    })
    return unwrapList<CategoryDto>(raw).map(categoryMapper.toCategory)
  }

  async create(outletId: string, input: CategoryInput): Promise<Category> {
    const raw = await httpClient.post<unknown>('/categories', {
      outlet_id: outletId,
      name: input.name,
    })
    return categoryMapper.toCategory(unwrap<CategoryDto>(raw))
  }

  async update(id: string, input: CategoryInput): Promise<Category> {
    const raw = await httpClient.put<unknown>(`/categories/${id}`, { name: input.name })
    return categoryMapper.toCategory(unwrap<CategoryDto>(raw))
  }

  async remove(id: string): Promise<void> {
    await httpClient.delete(`/categories/${id}`)
  }

  async setActive(id: string, active: boolean): Promise<void> {
    await httpClient.put(`/categories/${id}/active`, { active })
  }
}
